// src/pages/PinCode-Page/Pincode.js
import React, { useState, useEffect, useMemo } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  MdSearch,
  MdEdit,
  MdDelete,
  MdClear,
} from 'react-icons/md';
import {
  FaPlus,
  FaFileExcel,
  FaEye,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaMapPin,
  FaBuilding,
  FaGlobeAmericas,
} from 'react-icons/fa';
import { HiOutlineViewGrid, HiOutlineViewList } from 'react-icons/hi';
import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    '&:hover': { borderColor: '#2563eb' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    fontSize: '0.875rem',
    cursor: 'pointer',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
  }),
};

const initialFormState = {
  pincode: '',
  locationname: '',
  stateid: '',
  branchid: '',
  ismetro: 'Non-Metro',
  isoda: 'Non-ODA',
  status: true,
};

const Pincode = () => {
  const [pincodes, setPincodes] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  const [stateFilter, setStateFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState('card');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPincode, setEditingPincode] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [viewItem, setViewItem] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchPincodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch pincodes');
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error loading pincodes');
      setPincodes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/branch`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const { data } = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchStates(), fetchBranches(), fetchPincodes()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, branchFilter, stateFilter]);

  // Options for react-select
  const stateOptions = useMemo(() => {
    return states
      .slice()
      .sort((a, b) => (a.statename || '').localeCompare(b.statename || ''))
      .map((s) => ({ value: s._id, label: s.statename }));
  }, [states]);

  const branchOptions = useMemo(() => {
    return branches
      .slice()
      .sort((a, b) => (a.branchname || '').localeCompare(b.branchname || ''))
      .map((b) => ({ value: b._id, label: `${b.branchname} (${b.branchcode || 'N/A'})` }));
  }, [branches]);

  // Validation
  const validate = () => {
    const errors = {};
    if (!form.pincode) errors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(form.pincode)) errors.pincode = 'Must be exactly 6 digits';

    if (!form.locationname?.trim()) errors.locationname = 'Location Name is required';
    if (!form.stateid) errors.stateid = 'State is required';
    if (!form.branchid) errors.branchid = 'Branch is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenModal = (item = null) => {
    setFormErrors({});
    if (item) {
      setEditingPincode(item);
      setForm({
        pincode: item.pincode || '',
        locationname: item.locationname || '',
        stateid: item.stateid?._id || item.stateid || '',
        branchid: item.branchid?._id || item.branchid || '',
        ismetro: item.ismetro || 'Non-Metro',
        isoda: item.isoda || 'Non-ODA',
        status: item.status !== false,
      });
    } else {
      setEditingPincode(null);
      setForm(initialFormState);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPincode(null);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        pincode: form.pincode.trim(),
        locationname: form.locationname.trim(),
        stateid: form.stateid,
        branchid: form.branchid,
        ismetro: form.ismetro,
        isoda: form.isoda,
        status: form.status,
      };

      const url = editingPincode ? `${API_BASE_URL}/pincode/${editingPincode._id}` : `${API_BASE_URL}/pincode`;
      const method = editingPincode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Operation failed');

      toast.success(editingPincode ? 'Pincode updated successfully!' : 'Pincode created successfully!');
      handleCloseModal();
      fetchPincodes();
    } catch (err) {
      toast.error(err.message || 'Error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, code) => {
    const result = await Swal.fire({
      title: 'Delete Pincode?',
      text: `Are you sure you want to delete ${code || 'this pincode'}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${API_BASE_URL}/pincode/${id}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to delete pincode');
        toast.success('Pincode deleted successfully');
        fetchPincodes();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Export Excel
  const handleExportExcel = () => {
    if (pincodes.length === 0) {
      toast.info('No pincode data to export');
      return;
    }
    const exportRows = filteredPincodes.map((p, idx) => ({
      'S.No': idx + 1,
      Pincode: p.pincode || '-',
      'Location Name': p.locationname || '-',
      State: p.stateid?.statename || states.find((s) => s._id === p.stateid)?.statename || '-',
      Branch: p.branchid?.branchname || branches.find((b) => b._id === p.branchid)?.branchname || '-',
      'Metro Type': p.ismetro || 'Non-Metro',
      'ODA Area': p.isoda || 'Non-ODA',
      Status: p.status !== false ? 'Active' : 'Inactive',
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pincodes');
    XLSX.writeFile(workbook, `Pincode_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Pincodes exported to Excel successfully!');
  };

  // Filter Logic
  const filteredPincodes = useMemo(() => {
    return pincodes.filter((p) => {
      const isActive = p.status !== false;
      if (statusFilter === 'ACTIVE' && !isActive) return false;
      if (statusFilter === 'INACTIVE' && isActive) return false;

      if (branchFilter !== 'ALL') {
        const bId = p.branchid?._id || p.branchid;
        if (bId !== branchFilter) return false;
      }

      if (stateFilter !== 'ALL') {
        const sId = p.stateid?._id || p.stateid;
        if (sId !== stateFilter) return false;
      }

      if (!searchTerm.trim()) return true;
      const term = searchTerm.toLowerCase().trim();
      const stateName = p.stateid?.statename || states.find((s) => s._id === p.stateid)?.statename || '';
      const branchName = p.branchid?.branchname || branches.find((b) => b._id === p.branchid)?.branchname || '';

      return (
        (p.pincode || '').includes(term) ||
        (p.locationname || '').toLowerCase().includes(term) ||
        stateName.toLowerCase().includes(term) ||
        branchName.toLowerCase().includes(term)
      );
    });
  }, [pincodes, searchTerm, statusFilter, branchFilter, stateFilter, states, branches]);

  const totalItems = filteredPincodes.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredPincodes.slice(start, start + pageSize);
  }, [filteredPincodes, currentPage, pageSize]);

  const totalActive = useMemo(() => pincodes.filter((p) => p.status !== false).length, [pincodes]);
  const totalInactive = pincodes.length - totalActive;
  const totalMetro = useMemo(() => pincodes.filter((p) => p.ismetro === 'Metro').length, [pincodes]);

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header & KPI Summary */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center gap-2">
            Pincode Master
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage delivery pincodes, branch mappings, metro classifications & ODA areas
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-lg text-sm font-semibold transition shadow-sm"
          >
            <FaFileExcel className="text-green-600 text-base" />
            <span>Export Excel</span>
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition shadow-md hover:shadow-lg"
          >
            <FaPlus className="text-sm" />
            <span>Add New Pincode</span>
          </button>
        </div>
      </div>

      {/* KPI Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Total Pincodes</p>
            <p className="text-2xl font-bold text-gray-800">{pincodes.length}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
            <FaMapPin />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Active</p>
            <p className="text-2xl font-bold text-green-600">{totalActive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center font-bold">
            <FaCheckCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Inactive</p>
            <p className="text-2xl font-bold text-gray-500">{totalInactive}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center font-bold">
            <FaTimesCircle />
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase">Metro Pincodes</p>
            <p className="text-2xl font-bold text-indigo-600">{totalMetro}</p>
          </div>
          <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
            <FaBuilding />
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6 mb-8">
        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="relative flex-1 min-w-[260px]">
            <MdSearch className="absolute left-3.5 top-3 text-gray-400 text-xl" />
            <input
              type="text"
              placeholder="Search by pincode, location, state, branch..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-3 text-gray-400">
                <MdClear className="text-lg" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>

            {/* State Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">State:</span>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[150px]"
              >
                <option value="ALL">All States</option>
                {states.map((s) => (
                  <option key={s._id} value={s._id}>{s.statename}</option>
                ))}
              </select>
            </div>

            {/* Branch Filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold text-gray-500 uppercase">Branch:</span>
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500 max-w-[150px]"
              >
                <option value="ALL">All Branches</option>
                {branches.map((b) => (
                  <option key={b._id} value={b._id}>{b.branchname}</option>
                ))}
              </select>
            </div>

            {(searchTerm || statusFilter !== 'ALL' || branchFilter !== 'ALL' || stateFilter !== 'ALL') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                  setBranchFilter('ALL');
                  setStateFilter('ALL');
                }}
                className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 underline"
              >
                Reset
              </button>
            )}

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200 ml-auto lg:ml-2">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'card' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewGrid className="text-base" />
                <span className="hidden sm:inline">Cards</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition ${
                  viewMode === 'table' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600'
                }`}
              >
                <HiOutlineViewList className="text-base" />
                <span className="hidden sm:inline">List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="py-20 text-center text-gray-400 flex flex-col items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm font-semibold">Loading pincodes...</p>
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="py-16 text-center text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaMapPin className="text-4xl text-gray-300 mx-auto mb-2" />
            <p className="text-base font-bold text-gray-700">No pincodes found</p>
          </div>
        ) : viewMode === 'card' ? (
          /* Landscape Cards Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
            {paginatedData.map((p, idx) => {
              const isActive = p.status !== false;
              const sNo = (currentPage - 1) * pageSize + idx + 1;
              const stateName = p.stateid?.statename || states.find((s) => s._id === p.stateid)?.statename || '-';
              const branchName = p.branchid?.branchname || branches.find((b) => b._id === p.branchid)?.branchname || '-';

              return (
                <div
                  key={p._id || idx}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-blue-400 p-5 flex flex-col justify-between transition-all"
                >
                  <div>
                    <div className="flex items-start justify-between gap-3 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-mono font-bold text-base flex items-center justify-center shadow-sm">
                          {p.pincode ? p.pincode.slice(0, 3) : 'PIN'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-mono font-bold text-blue-700 text-lg leading-tight">
                              {p.pincode}
                            </h3>
                            <span className="text-xs text-gray-400 font-medium">#{sNo}</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-800 leading-tight mt-0.5">
                            {p.locationname}
                          </p>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                          isActive
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></span>
                        {isActive ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 my-3.5 bg-gray-50/70 p-3 rounded-lg border border-gray-100 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">State</span>
                        <span className="font-semibold text-gray-800 truncate block">{stateName}</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Branch</span>
                        <span className="font-semibold text-gray-800 truncate block">{branchName}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${p.ismetro === 'Metro' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                        {p.ismetro || 'Non-Metro'}
                      </span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${p.isoda === 'ODA' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {p.isoda || 'Non-ODA'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 mt-2">
                    <button
                      onClick={() => setViewItem(p)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
                    >
                      <FaEye /> <span>View</span>
                    </button>
                    <button
                      onClick={() => handleOpenModal(p)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
                    >
                      <MdEdit /> <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(p._id, p.pincode)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition"
                    >
                      <MdDelete /> <span>Delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Table List View */
          <div className="overflow-x-auto rounded-lg border border-gray-200 mb-6">
            <table className="w-full text-left text-sm text-gray-600 border-collapse">
              <thead className="bg-blue-600 text-white text-xs uppercase font-semibold">
                <tr>
                  <th className="py-3 px-4 text-center w-14">S.No</th>
                  <th className="py-3 px-4">Pincode</th>
                  <th className="py-3 px-4">Location Name</th>
                  <th className="py-3 px-4">State</th>
                  <th className="py-3 px-4">Branch</th>
                  <th className="py-3 px-4">Classification</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {paginatedData.map((p, idx) => {
                  const isActive = p.status !== false;
                  const sNo = (currentPage - 1) * pageSize + idx + 1;
                  const stateName = p.stateid?.statename || states.find((s) => s._id === p.stateid)?.statename || '-';
                  const branchName = p.branchid?.branchname || branches.find((b) => b._id === p.branchid)?.branchname || '-';

                  return (
                    <tr key={p._id || idx} className="hover:bg-blue-50/50 transition">
                      <td className="py-3.5 px-4 text-center text-gray-500 font-semibold">{sNo}</td>
                      <td className="py-3.5 px-4 font-mono font-bold text-blue-600">{p.pincode}</td>
                      <td className="py-3.5 px-4 font-bold text-gray-800">{p.locationname}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{stateName}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-700">{branchName}</td>
                      <td className="py-3.5 px-4 text-xs">
                        <span className="font-semibold text-gray-700 mr-2">{p.ismetro || 'Non-Metro'}</span>
                        <span className="text-gray-400">({p.isoda || 'Non-ODA'})</span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {isActive ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-1.5">
                          <button onClick={() => setViewItem(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded">
                            <FaEye />
                          </button>
                          <button onClick={() => handleOpenModal(p)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                            <MdEdit />
                          </button>
                          <button onClick={() => handleDelete(p._id, p.pincode)} className="p-1.5 text-red-600 hover:bg-red-50 rounded">
                            <MdDelete />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 outline-none font-semibold"
              >
                {[6, 10, 20, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>entries per page</span>
              <span className="hidden md:inline ml-3 text-gray-400">
                (Page {currentPage} of {totalPages})
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((pageNum, idx, arr) => (
                  <React.Fragment key={pageNum}>
                    {idx > 0 && pageNum - arr[idx - 1] > 1 && <span className="px-1 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg text-xs font-bold transition ${
                        currentPage === pageNum ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-3 py-1.5 rounded-lg border font-semibold text-xs transition disabled:opacity-40 hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Add/Edit Pincode */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaMapPin className="text-xl" />
                <h3 className="text-xl font-bold">{editingPincode ? 'Edit Pincode' : 'Add New Pincode'}</h3>
              </div>
              <button onClick={handleCloseModal} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Pincode (6 Digits) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="e.g. 600001"
                    value={form.pincode}
                    onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '') })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.pincode ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.pincode && <p className="text-red-500 text-xs mt-1">{formErrors.pincode}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Location Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. George Town"
                    value={form.locationname}
                    onChange={(e) => setForm({ ...form, locationname: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 ${
                      formErrors.locationname ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.locationname && <p className="text-red-500 text-xs mt-1">{formErrors.locationname}</p>}
                </div>

                {/* Searchable State Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={stateOptions}
                    value={stateOptions.find((s) => s.value === form.stateid) || null}
                    onChange={(opt) => setForm({ ...form, stateid: opt ? opt.value : '' })}
                    placeholder="Search & select state..."
                    styles={customSelectStyles}
                  />
                  {formErrors.stateid && <p className="text-red-500 text-xs mt-1">{formErrors.stateid}</p>}
                </div>

                {/* Searchable Branch Dropdown */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">
                    Branch <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={branchOptions}
                    value={branchOptions.find((b) => b.value === form.branchid) || null}
                    onChange={(opt) => setForm({ ...form, branchid: opt ? opt.value : '' })}
                    placeholder="Search & select branch..."
                    styles={customSelectStyles}
                  />
                  {formErrors.branchid && <p className="text-red-500 text-xs mt-1">{formErrors.branchid}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Metro Type</label>
                  <select
                    value={form.ismetro}
                    onChange={(e) => setForm({ ...form, ismetro: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Non-Metro">Non-Metro</option>
                    <option value="Metro">Metro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Delivery Area (ODA)</label>
                  <select
                    value={form.isoda}
                    onChange={(e) => setForm({ ...form, isoda: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Non-ODA">Non-ODA</option>
                    <option value="ODA">ODA</option>
                  </select>
                </div>

                <div className="md:col-span-2 flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="pincode-status"
                    checked={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="pincode-status" className="text-xs font-bold text-gray-700 cursor-pointer">
                    Pincode Enabled (Active for booking & delivery)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow transition flex items-center gap-2"
                >
                  {submitting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  <span>{editingPincode ? 'Update Pincode' : 'Create Pincode'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: View Details */}
      {viewItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-700 to-indigo-700 text-white flex items-center justify-between">
              <h3 className="text-lg font-bold">Pincode {viewItem.pincode}</h3>
              <button onClick={() => setViewItem(null)} className="text-white/80 hover:text-white p-1 rounded-full">
                <FaTimes />
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Location Name</span>
                  <strong className="text-gray-800 text-base">{viewItem.locationname}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">State</span>
                  <strong className="text-gray-800 text-base">{viewItem.stateid?.statename || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Branch</span>
                  <strong className="text-gray-800 text-base">{viewItem.branchid?.branchname || '-'}</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Classification</span>
                  <strong className="text-gray-800">{viewItem.ismetro || 'Non-Metro'} ({viewItem.isoda || 'Non-ODA'})</strong>
                </div>
                <div>
                  <span className="text-gray-400 block text-xs uppercase">Status</span>
                  <span className={`inline-block px-2.5 py-0.5 rounded text-xs font-bold ${viewItem.status !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {viewItem.status !== false ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end gap-2">
              <button onClick={() => setViewItem(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold">
                Close
              </button>
              <button
                onClick={() => {
                  const target = viewItem;
                  setViewItem(null);
                  handleOpenModal(target);
                }}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pincode;